import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import type { AnswerRow } from './ask-user.utils';

interface AskUserSummaryProps {
  rows: AnswerRow[];
  onEdit?: () => void;
  defaultExpanded?: boolean;
}

export function AskUserSummary({
  rows,
  onEdit,
  defaultExpanded = false,
}: AskUserSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const answeredCount = rows.filter((row) => !row.skipped).length;

  return (
    <YStack className="w-fit max-w-full bg-surface-hover text-foreground smoothing-default rounded-xl break-words">
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((expanded) => !expanded)}
        className={cn(
          'focus-ring flex items-center justify-between gap-2 px-3.5 py-1.5 text-left hover:bg-surface-active',
          isExpanded ? 'rounded-t-xl' : 'rounded-xl'
        )}
      >
        <span className="text-base">
          {answeredCount ? `${answeredCount} answered` : 'All skipped'}
        </span>
        <Icons.chevronRight
          className={cn(
            'text-foreground-muted size-4 shrink-0 transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
      </button>

      {isExpanded && (
        <YStack className="gap-3 px-3.5 pt-1 pb-3.5">
          {rows.map((row) => (
            <YStack key={row.questionId} className="gap-0.5 break-words">
              <span className="text-foreground-muted text-base">
                {row.prompt}
              </span>
              <span
                className={cn(
                  'text-base',
                  row.skipped ? 'text-foreground-muted' : 'text-foreground'
                )}
              >
                {row.answer}
              </span>
            </YStack>
          ))}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="text-foreground-muted hover:text-foreground focus-ring self-end text-base"
            >
              Edit answers
            </button>
          )}
        </YStack>
      )}
    </YStack>
  );
}
